'use client';

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';
import { Currency } from 'hotdesk_typescript';
import styles from './CurrencySelect.module.scss';
const MOBILE_SHEET_QUERY = '(max-width: 768px)';
export interface CurrencySelectProps {
  label: string;
  searchPlaceholder: string;
  emptyLabel: string;
  value: string;
  currencies: Currency[];
  onChange: (code: string) => void;
  disabled?: boolean;
  hideLabel?: boolean;
  inset?: boolean;
  triggerVariant?: 'default' | 'locale';
}
const CurrencySelect: FC<CurrencySelectProps> = ({
  label,
  searchPlaceholder,
  emptyLabel,
  value,
  currencies,
  onChange,
  disabled = false,
  hideLabel = false,
  inset = false,
  triggerVariant = 'default'
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [mounted, setMounted] = useState(false);
  const [isMobileSheet, setIsMobileSheet] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setMounted(true);
  }, []);
  useEffect(() => {
    const media = window.matchMedia(MOBILE_SHEET_QUERY);
    const sync = () => setIsMobileSheet(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);
  const close = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);
  useEffect(() => {
    if (!open) return;
    if (isMobileSheet) {
      document.body.style.overflow = 'hidden';
    }
    const id = window.setTimeout(() => searchRef.current?.focus(), 100);
    return () => {
      document.body.style.overflow = '';
      window.clearTimeout(id);
    };
  }, [open, isMobileSheet]);
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      close();
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open, close]);
  const selected = useMemo(() => currencies.find(c => c.code === value), [currencies, value]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return currencies;
    return currencies.filter(c => c.code.toLowerCase().includes(q) || c.name && c.name.toLowerCase().includes(q));
  }, [currencies, search]);
  const handleSelect = (code: string) => {
    onChange(code);
    close();
  };
  const panelContent = <>
            {isMobileSheet && <div className={styles.header}>
                    <h3 className={styles.title}>{label}</h3>
                    <button type="button" className={styles.closeButton} onClick={close} aria-label="Close">
                        <X size={18} strokeWidth={2} />
                    </button>
                </div>}

            <div className={styles.searchWrap}>
                <input ref={searchRef} type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder={searchPlaceholder} className={styles.searchInput} autoComplete="off" enterKeyHint="search" />
            </div>

            <ul className={styles.list} role="listbox">
                {filtered.length === 0 ? <li className={styles.empty}>{emptyLabel}</li> : filtered.map(currency => {
        const isActive = currency.code === value;
        return <li key={currency.code} role="option" aria-selected={isActive}>
                                <button type="button" className={`${styles.item} ${isActive ? styles.itemActive : ''}`} onClick={() => handleSelect(currency.code)}>
                                    <span className={styles.itemText}>
                                        <span className={styles.code}>{currency.code}</span>
                                        {currency.name && currency.name !== currency.code && <span className={styles.name}>{currency.name}</span>}
                                    </span>
                                    {isActive && <Check size={18} strokeWidth={2.5} className={styles.check} />}
                                </button>
                            </li>;
      })}
            </ul>
        </>;
  const mobileSheet = open && isMobileSheet ? <div className={styles.overlay} onClick={close} role="presentation">
                <div ref={panelRef} className={`${styles.panel} ${styles.panelSheet}`} role="dialog" aria-modal="true" aria-label={label} onClick={e => e.stopPropagation()}>
                    {panelContent}
                </div>
            </div> : null;
  return <div ref={rootRef} className={`${styles.field} ${inset ? styles.fieldInset : ''}`} data-sentry-component="CurrencySelect" data-sentry-source-file="CurrencySelect.tsx">
            {!hideLabel && <span className={styles.label}>{label}</span>}
            <button type="button" className={`${styles.trigger} ${triggerVariant === 'locale' ? styles.triggerLocale : ''}`} onClick={() => !disabled && setOpen(prev => !prev)} aria-haspopup="listbox" aria-expanded={open} disabled={disabled}>
                <span className={styles.triggerValue}>
                    {selected?.code || value || '—'}
                </span>
                <ChevronDown size={16} className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} data-sentry-element="ChevronDown" data-sentry-source-file="CurrencySelect.tsx" />
            </button>

            {open && !isMobileSheet && <div ref={panelRef} className={`${styles.panel} ${styles.panelDropdown}`} role="dialog" aria-label={label}>
                    {panelContent}
                </div>}

            {mounted && mobileSheet ? createPortal(mobileSheet, document.body) : null}
        </div>;
};
export default CurrencySelect;